import { CheckSquareFilled } from '@ant-design/icons';
import React, { useState } from 'react';
import { COLUMN_WIDTHS } from '../../TaskTable/constants/columnWidths';
import { Column } from '../../../types/WorkspaceViewColumn';
import { WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewGroup, WorkspaceViewEntityType } from '../../../types/WorkspaceView';
import { SPACE_COLUMN_WIDTH } from '../../../utils/taskListUtils';
import { isColumnVisible as isColumnVisibleShared, isExpandColumnVisible } from './columnVisibility';

interface StickyColumnHeaderProps {
  showMultiselect: boolean;
  allTasksSelected: boolean;
  hasAnyTasks: boolean;
  hasAnySelected?: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  hasRowSelection?: boolean;
  framework?: WorkspaceFramework;
  viewGroup?: WorkspaceViewGroup;
  viewEntityType?: WorkspaceViewEntityType;
  hiddenColumns?: Column[];
  columnsOrder?: Column[];
  estimationType?: string;
  titleWidth?: number;
  showSpaceColumn?: boolean;
}

export const StickyColumnHeader: React.FC<StickyColumnHeaderProps> = ({
  showMultiselect,
  allTasksSelected,
  hasAnyTasks,
  hasAnySelected = false,
  onSelectAll,
  onDeselectAll,
  hasRowSelection,
  framework,
  viewGroup,
  viewEntityType,
  hiddenColumns = [],
  columnsOrder = [],
  estimationType = 'storyPoints',
  titleWidth = 394,
  showSpaceColumn,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const operationWidth = hasRowSelection ? 86 : 68;
  const visibilityContext = { hiddenColumns, viewEntityType, viewGroup, framework };
  const expandVisible = isExpandColumnVisible(visibilityContext);

  const isColumnVisible = (column: Column): boolean => isColumnVisibleShared(column, visibilityContext);

  // Define all columns with their properties
  const allColumns = [
    { key: 'operation', title: '', width: operationWidth, sticky: true },
    { key: 'taskId', title: 'ID', width: COLUMN_WIDTHS.taskId, sticky: true, column: null },
    {
      key: 'expand',
      title: '',
      width: 20,
      sticky: true,
      column: Column.subitems,
      visible: expandVisible,
    },
    { key: 'title', title: 'Task name', width: titleWidth, sticky: true, column: null },
    ...(showSpaceColumn
      ? [{ key: 'space-label', title: 'Space', width: SPACE_COLUMN_WIDTH, column: Column.space }]
      : []),
    {
      key: 'subtasks',
      title: viewEntityType === WorkspaceViewEntityType.epic ? 'Progress' : 'Subitems',
      width: COLUMN_WIDTHS.subitems,
      column: Column.subitems,
    },
    { key: 'epicId', title: 'Epic', width: COLUMN_WIDTHS.epicId, column: Column.epic },
    { key: 'sprintId', title: 'Stage', width: COLUMN_WIDTHS.sprintId, column: Column.sprintStatus },
    { key: 'TaskLabels', title: 'Label', width: COLUMN_WIDTHS.TaskLabels, column: Column.label },
    { key: 'assignee', title: 'Owner', width: COLUMN_WIDTHS.assignee, column: Column.assignee },
    { key: 'statusId', title: 'Status', width: COLUMN_WIDTHS.statusId, column: Column.status },
    { key: 'priority', title: 'Priority', width: COLUMN_WIDTHS.priority, column: Column.priority },
    {
      key: estimationType === 'tshirtSizes' ? 'estimate' : 'storyPoints',
      title: 'Estimate',
      width: COLUMN_WIDTHS.estimate,
      column: Column.estimate,
    },
    { key: 'startDate', title: 'Start Date', width: COLUMN_WIDTHS.startDate, column: Column.startDate },
    { key: 'deadline', title: 'Target Date', width: COLUMN_WIDTHS.deadline, column: Column.deadline },
    { key: 'blocking', title: 'Blocking', width: COLUMN_WIDTHS.blocking, column: Column.blocking },
    { key: 'blockedBy', title: 'Blocked By', width: COLUMN_WIDTHS.blockedBy, column: Column.blockedBy },
    { key: 'space', title: '', width: COLUMN_WIDTHS.space, column: null },
  ];

  // Filter visible columns
  const visibleColumns = allColumns.filter((col) => {
    if (col.visible !== undefined) return col.visible;
    if (col.column === null) return true;
    return col.column ? isColumnVisible(col.column) : true;
  });

  // Separate frozen (sticky) columns from reorderable columns
  const frozenColumns = visibleColumns.filter((col) => col.sticky);
  const reorderableColumns = visibleColumns.filter((col) => !col.sticky);

  // Sort reorderable columns by columnsOrder if available
  const sortedReorderable =
    columnsOrder.length > 0
      ? [...reorderableColumns].sort((a, b) => {
          const indexA = a.column ? columnsOrder.indexOf(a.column) : -1;
          const indexB = b.column ? columnsOrder.indexOf(b.column) : -1;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
      : reorderableColumns;

  const sortedColumns = [...frozenColumns, ...sortedReorderable];

  // Calculate total width of all visible columns
  const totalWidth = sortedColumns.reduce((sum, col) => sum + (col.width || 0), 0);

  return (
    <div
      className="sticky top-0 z-10 bg-neutral-200 border-b border-neutral-200"
      style={{
        minWidth: `${totalWidth}px`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex h-10 items-center text-sm font-semibold leading-[14px]">
        {sortedColumns.map((col) => {
          const isSticky = col.sticky;

          return (
            <div
              key={col.key}
              className={`flex items-center flex-shrink-0 ${isSticky ? 'sticky bg-neutral-200 z-10 h-full' : 'border-0 border-l border-solid border-neutral-200'} ${
                col.key === 'title'
                  ? // Align the header with the row's title *text*, which is inset past the type
                    // icon (pl-2 left it sitting over the icon instead, ~16px left of the titles). #2691
                    'sticky-title-cell justify-start pl-6'
                  : col.key === 'taskId'
                    ? 'sticky-title-cell justify-start pl-2'
                    : 'justify-center'
              }`}
              style={{
                width: `${col.width}px`,
                minWidth: `${col.width}px`,
                left: isSticky ? getLeftOffset(col.key, operationWidth, expandVisible) : undefined,
              }}
            >
              {col.key === 'operation' ? (
                <div className="flex items-center pl-9">
                  {showMultiselect && hasAnyTasks && (
                    <div
                      className={`flex items-center ${
                        !allTasksSelected && !isHovered && !hasAnySelected ? 'invisible' : ''
                      }`}
                    >
                      {allTasksSelected ? (
                        <CheckSquareFilled className="text-blue-500 cursor-pointer text-base" onClick={onDeselectAll} />
                      ) : (
                        <div
                          className="h-4 w-4 rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500 cursor-pointer"
                          onClick={onSelectAll}
                        />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="cursor-default">{col.title}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper to calculate left offset for sticky columns
const getLeftOffset = (key: string, operationWidth: number, expandVisible: boolean): number => {
  const offsets: Record<string, number> = {
    operation: 0,
    taskId: operationWidth,
    expand: operationWidth + COLUMN_WIDTHS.taskId,
    title: operationWidth + COLUMN_WIDTHS.taskId + (expandVisible ? 20 : 0),
  };
  return offsets[key] || 0;
};
